import React from 'react';
import { getDataTable } from '../../charts/chartDataBindings';
import { SingleEntityTemplate } from '../../../../api/models/assets';
import { TpDataContext, TpDataTableColumn } from '../../../../data/interfaces';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { getColorFromNumericalSet, getNumericalColorSet } from '../../helpers/colors';

import styles from '../SingleItemView.module.scss';
import { SectionIndex, TpSectionsIndexContext } from '../../../../components/sections-index/SectionsIndex';

interface TpPropertyNumericalTilesProps {
  itemId: string;
  dataContext: TpDataContext;
  entityTypeTemplate: SingleEntityTemplate;
  children?: React.ReactNode;
  elementTemplate: any;
  sectionIndexContext: TpSectionsIndexContext;
}

interface TpElementTemplate {
  title: string;
  dataTableId: string;
  propertyGroupId: string;
  tileWidth?: number;
  maxLineCount?: number;
  minVal: number;
  maxVal: number;
  colorScheme?: string;
  sortBy?: string;
}

const PropertyNumericalTiles = (props: TpPropertyNumericalTilesProps) => {
  const { dataContext, itemId } = props;

  const elementTemplate = props.elementTemplate as TpElementTemplate;
  const dataTable = getDataTable(dataContext, elementTemplate.dataTableId);
  const itemIndex = dataTable.itemIdMap[props.itemId];

  const columns = dataTable.columns.filter(
    (column) => column.groupId == elementTemplate.propertyGroupId && !isNaN(dataTable.columnData[column.id][itemIndex]),
  );

  if (elementTemplate.sortBy == 'valInc')
    columns.sort((col1, col2) => dataTable.columnData[col1.id][itemIndex] - dataTable.columnData[col2.id][itemIndex]);
  if (elementTemplate.sortBy == 'valDec')
    columns.sort((col1, col2) => dataTable.columnData[col2.id][itemIndex] - dataTable.columnData[col1.id][itemIndex]);

  const maxLineCount = elementTemplate.maxLineCount || 2;
  const tileWidth = elementTemplate.tileWidth ?? 100;
  const textFontSize = 14; // in px
  const lineHeight = textFontSize + 3;

  const tileStyle = {
    // @todo: move the fixed part of this to css
    display: 'inline-block',
    width: `${tileWidth}px`,
    height: `${maxLineCount * lineHeight + 25}px`,
    backgroundColor: 'rgb(255,230,200)',
    margin: '5px',
    padding: '5px',
    verticalAlign: 'top',
    borderRadius: '5px',
  };

  const colorSet = getNumericalColorSet(elementTemplate.colorScheme || 'Default');

  function formatName(name: string): string {
    return name.replace(/_/g, ' '); // @todo: remove this once we get bettern names from the source data!
  }

  function renderTile(col: TpDataTableColumn): any {
    const propValue = dataTable.columnData[col.id][itemIndex];
    const colorFrac = Math.max(
      0,
      Math.min(1, (propValue - elementTemplate.minVal) / (elementTemplate.maxVal - elementTemplate.minVal)),
    );
    const colorStr = getColorFromNumericalSet(colorSet, colorFrac).lighten(0.6).toCanvasString();
    return (
      <div style={{ ...tileStyle, backgroundColor: colorStr, position: 'relative' }} key={col.id}>
        <div
          style={{
            width: '100%',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: maxLineCount,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: `${lineHeight}px`,
            fontSize: `${textFontSize}px`,
            maxHeight: `${maxLineCount * lineHeight}px`, // it seems we must set max-height for Safari
          }}
        >
          {formatName(col.name)}
          <p />
        </div>
        <div style={{ position: 'absolute', bottom: '5px', left: '5px', fontWeight: 'bold' }}>
          {convertColumnDataToDisplayString(col, propValue)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <SectionIndex
        sectionIndexContext={props.sectionIndexContext}
        sectionId={`proptiles_${dataTable.id}_${elementTemplate.propertyGroupId}`}
        sectionName={elementTemplate.title}
      >
        <div className={styles.tableTitle}>{elementTemplate.title}</div>
        {columns.map((col) => renderTile(col))}
        {columns.length == 0 && <div>No data available</div>}
      </SectionIndex>
    </div>
  );
};

export default PropertyNumericalTiles;
